import React from 'react';
import {
	Typography,
	FormControl,
	InputLabel,
	MenuItem,
	TableHead,
	TableBody,
	Tooltip,
	IconButton,
	Tabs,
	Tab,
	InputAdornment,
} from '@mui/material';
import {
	FileUploadRounded,
	CloseRounded,
	VpnKeyRounded,
	DeleteRounded,
	PeopleRounded,
	SearchRounded,
} from '@mui/icons-material';
import ThreeDotMenu from './ThreeDotMenu';
import SubirRecursoModal, { RecursoInput } from './SubirRecursoModal';
import ConfirmarEliminarDialog from './ConfirmarEliminarDialog';
import ConfirmarLiberarMateriaDialog from './ConfirmarLiberarMateriaDialog';
import { EResource } from '../../types/departamento';
import { IMiRecurso, EMiRecursoStatus, IDocenteMateria, IAlumnoEnrollment } from '../../types/miRecurso';
import {
	PageContainer,
	DetailHeader,
	DetailHeaderLeft,
	DetailHeaderRight,
	CodigoChip,
	FiltersContainer,
	SingleFilterContainer,
	SearchField,
	FilterSelect,
	AppliedFiltersContainer,
	FilterChip,
	ClearFiltersButton,
	ResultsHeader,
	UploadButton,
	ReleaseButton,
	StyledTableContainer,
	StyledTable,
	StyledTableHeaderCell,
	StyledTableCell,
	StyledTableRow,
	FileNameCell,
	FileIconContainer,
	StatusBadge,
	ReviewerCell,
	ReviewerEmail,
	EmptyStateContainer,
	TabsContainer,
	AlumnoListContainer,
	AlumnoListHeader,
	AlumnoRow,
	AlumnoAvatar,
	AlumnoInfo,
} from './styles';

interface MateriaDetailPageProps {
	materia: IDocenteMateria;
	recursos: IMiRecurso[];
	alumnos: IAlumnoEnrollment[];
	selectedStatus: EMiRecursoStatus | 'all';
	selectedDetailTab: 'recursos' | 'alumnos';
	searchQuery: string;
	pendingRemoveAlumno: IAlumnoEnrollment | null;
	onSearchChange: (query: string) => void;
	onStatusChange: (status: EMiRecursoStatus | 'all') => void;
	onResetFilters: () => void;
	onTabChange: (tab: 'recursos' | 'alumnos') => void;
	onSubirNuevoRecurso: () => void;
	onSubirNuevaVersion: (recurso: IMiRecurso) => void;
	onBorrar: (recurso: IMiRecurso) => void;
	onVerCodigo: () => void;
	onRemoveAlumno: (enrollment: IAlumnoEnrollment) => void;
	onConfirmRemoveAlumno: () => Promise<void>;
	onCancelRemoveAlumno: () => void;
	releaseOpen: boolean;
	onEliminarMateria: () => void;
	onConfirmLiberarMateria: () => Promise<void>;
	onCancelLiberarMateria: () => void;
	isSubirRecursoModalOpen: boolean;
	uploadTitle?: string;
	onCloseModal: () => void;
	onConfirmarSubida: (input: RecursoInput) => Promise<void>;
}

const getStatusLabel = (status: EMiRecursoStatus | 'all'): string => {
	const statusMap: Record<string, string> = {
		all: 'Todos los estatus',
		aprobado: 'Aprobado',
		en_revision: 'En revisión',
		cambios_solicitados: 'Cambios solicitados',
		denegado: 'Denegado',
	};
	return statusMap[status] ?? status;
};

const getFileTypeLabel = (type: EResource): string => {
	const typeMap: Record<string, string> = {
		pdf: 'PDF', docx: 'Word', pptx: 'PowerPoint',
		xlsx: 'Excel', video: 'Video', imagen: 'Imagen', other: 'Otro', link: 'Enlace',
	};
	return typeMap[type] ?? type;
};

const getAlumnoInitials = (nombre: string): string =>
	nombre
		.split(' ')
		.filter((w) => w.length > 0)
		.slice(0, 2)
		.map((w) => w[0])
		.join('')
		.toUpperCase();

const MateriaDetailPage: React.FC<MateriaDetailPageProps> = ({
	materia,
	recursos,
	alumnos,
	selectedStatus,
	selectedDetailTab,
	searchQuery,
	pendingRemoveAlumno,
	onSearchChange,
	onStatusChange,
	onResetFilters,
	onTabChange,
	onSubirNuevoRecurso,
	onSubirNuevaVersion,
	onBorrar,
	onVerCodigo,
	onRemoveAlumno,
	onConfirmRemoveAlumno,
	onCancelRemoveAlumno,
	releaseOpen,
	onEliminarMateria,
	onConfirmLiberarMateria,
	onCancelLiberarMateria,
	isSubirRecursoModalOpen,
	uploadTitle,
	onCloseModal,
	onConfirmarSubida,
}) => {
	const formatDate = (date: Date) =>
		date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });

	const hasActiveFilters = selectedStatus !== 'all';

	return (
		<PageContainer>
			{/* Materia header strip */}
			<DetailHeader>
				<DetailHeaderLeft>
					<Typography variant='h6' fontWeight={700}>
						{materia.nombre}
					</Typography>
					<Typography variant='body2' color='text.secondary'>·</Typography>
					<Typography variant='body2' color='text.secondary'>
						{materia.resourceCount} {materia.resourceCount === 1 ? 'recurso' : 'recursos'}
					</Typography>
					<Typography variant='body2' color='text.secondary'>·</Typography>
					<Typography variant='body2' color='text.secondary'>
						<PeopleRounded style={{ fontSize: '0.9rem', verticalAlign: 'middle', marginRight: 4 }} />
						{materia.alumnoCount} {materia.alumnoCount === 1 ? 'alumno' : 'alumnos'}
					</Typography>
				</DetailHeaderLeft>
				<DetailHeaderRight>
					<ReleaseButton
						variant='outlined'
						color='error'
						startIcon={<DeleteRounded />}
						onClick={onEliminarMateria}
					>
						Eliminar materia
					</ReleaseButton>
					<Tooltip title='Ver código de la materia' arrow>
						<CodigoChip
							label={materia.codigo}
							icon={<VpnKeyRounded style={{ fontSize: '0.85rem' }} />}
							onClick={onVerCodigo}
						/>
					</Tooltip>
					{selectedDetailTab === 'recursos' && (
						<UploadButton
							variant='contained'
							color='primary'
							onClick={onSubirNuevoRecurso}
							startIcon={<FileUploadRounded />}
						>
							Subir recurso
						</UploadButton>
					)}
				</DetailHeaderRight>
			</DetailHeader>

			{/* Tabs */}
			<TabsContainer>
				<Tabs
					value={selectedDetailTab}
					onChange={(_, val) => onTabChange(val)}
					textColor='primary'
					indicatorColor='primary'
				>
					<Tab label='Recursos' value='recursos' />
					<Tab
						label={`Alumnos (${materia.alumnoCount})`}
						value='alumnos'
					/>
				</Tabs>
			</TabsContainer>

		{/* ── Recursos tab ────────────────────────────────────────────── */}
		{selectedDetailTab === 'recursos' && (
			<>
				<FiltersContainer>
					<SearchField
						fullWidth
						placeholder='Buscar por nombre de archivo...'
						value={searchQuery}
						onChange={(e) => onSearchChange(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<SearchRounded />
								</InputAdornment>
							),
						}}
					/>
					<FormControl>
						<InputLabel id='status-label'>Estatus</InputLabel>
						<FilterSelect
							labelId='status-label'
							value={selectedStatus}
							label='Estatus'
							onChange={(e) => onStatusChange(e.target.value as EMiRecursoStatus | 'all')}
						>
							<MenuItem value='all'>Todos los estatus</MenuItem>
							<MenuItem value='en_revision'>En revisión</MenuItem>
							<MenuItem value='aprobado'>Aprobado</MenuItem>
							<MenuItem value='cambios_solicitados'>Cambios solicitados</MenuItem>
							<MenuItem value='denegado'>Denegado</MenuItem>
						</FilterSelect>
					</FormControl>
				</FiltersContainer>

					{hasActiveFilters && (
						<AppliedFiltersContainer>
							<FilterChip
								label={getStatusLabel(selectedStatus)}
								onDelete={() => onStatusChange('all')}
								deleteIcon={<CloseRounded />}
							/>
							<ClearFiltersButton onClick={onResetFilters}>
								<CloseRounded fontSize='small' />
								<span>Limpiar filtros</span>
							</ClearFiltersButton>
						</AppliedFiltersContainer>
					)}

					<ResultsHeader>
						<Typography variant='body1' color='text.secondary'>
							{recursos.length.toLocaleString('es-MX')} recurso
							{recursos.length !== 1 ? 's' : ''} encontrado
							{recursos.length !== 1 ? 's' : ''}
						</Typography>
					</ResultsHeader>

					{recursos.length > 0 ? (
						<StyledTableContainer>
							<StyledTable>
								<TableHead>
									<StyledTableRow>
										<StyledTableHeaderCell>Nombre de archivo</StyledTableHeaderCell>
										<StyledTableHeaderCell>Tipo</StyledTableHeaderCell>
										<StyledTableHeaderCell>Fecha de subida</StyledTableHeaderCell>
										<StyledTableHeaderCell>Peso</StyledTableHeaderCell>
										<StyledTableHeaderCell>Estatus</StyledTableHeaderCell>
										<StyledTableHeaderCell>Auditor</StyledTableHeaderCell>
										<StyledTableHeaderCell align='center'>Acciones</StyledTableHeaderCell>
									</StyledTableRow>
								</TableHead>
								<TableBody>
									{recursos.map((recurso) => (
										<StyledTableRow key={recurso.id}>
											<StyledTableCell>
												<FileNameCell>
													<FileIconContainer resourcetype={recurso.resourceType}>
														{recurso.resourceType === EResource.link
															? 'URL'
															: recurso.resourceType.toUpperCase().slice(0, 3)}
													</FileIconContainer>
													<Typography variant='body2' fontWeight={500} noWrap title={recurso.name}>
														{recurso.name}
													</Typography>
												</FileNameCell>
											</StyledTableCell>
											<StyledTableCell>
												<Typography variant='body2' color='text.secondary'>
													{getFileTypeLabel(recurso.resourceType)}
												</Typography>
											</StyledTableCell>
											<StyledTableCell>
												<Typography variant='body2' color='text.secondary' noWrap>
													{formatDate(recurso.uploadDate)}
												</Typography>
											</StyledTableCell>
											<StyledTableCell>
												<Typography variant='body2' color='text.secondary'>
													{recurso.resourceType === EResource.link ? '—' : recurso.size}
												</Typography>
											</StyledTableCell>
											<StyledTableCell>
												{recurso.status === 'cambios_solicitados' && recurso.reviewNote ? (
													<Tooltip title={recurso.reviewNote} arrow>
														<StatusBadge status={recurso.status}>
															{getStatusLabel(recurso.status)}
														</StatusBadge>
													</Tooltip>
												) : (
													<StatusBadge status={recurso.status}>
														{getStatusLabel(recurso.status)}
													</StatusBadge>
												)}
											</StyledTableCell>
											<StyledTableCell>
												{recurso.reviewerName ? (
													<ReviewerCell>
														<Typography variant='body2' noWrap title={recurso.reviewerName}>
															{recurso.reviewerName}
														</Typography>
														{recurso.reviewerEmail && (
															<ReviewerEmail href={`mailto:${recurso.reviewerEmail}`}>
																{recurso.reviewerEmail}
															</ReviewerEmail>
														)}
													</ReviewerCell>
												) : (
													<Typography variant='body2' color='text.secondary'>
														—
													</Typography>
												)}
											</StyledTableCell>
											<StyledTableCell align='center'>
												<ThreeDotMenu
													recurso={recurso}
													onSubirNuevaVersion={onSubirNuevaVersion}
													onBorrar={onBorrar}
												/>
											</StyledTableCell>
										</StyledTableRow>
									))}
								</TableBody>
							</StyledTable>
						</StyledTableContainer>
					) : (
						<EmptyStateContainer>
							<Typography variant='h6' color='text.secondary' gutterBottom>
								No se encontraron recursos
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								{hasActiveFilters
									? 'Intenta ajustar los filtros.'
									: 'Sube tu primer recurso para esta materia.'}
							</Typography>
						</EmptyStateContainer>
					)}
				</>
			)}

		{/* ── Alumnos tab ─────────────────────────────────────────────── */}
		{selectedDetailTab === 'alumnos' && (
			<>
				<SingleFilterContainer>
					<SearchField
						fullWidth
						placeholder='Buscar alumno por nombre o correo...'
						value={searchQuery}
						onChange={(e) => onSearchChange(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<SearchRounded />
								</InputAdornment>
							),
						}}
					/>
				</SingleFilterContainer>

				<ResultsHeader>
					<Typography variant='body1' color='text.secondary'>
						{alumnos.length.toLocaleString('es-MX')} alumno
						{alumnos.length !== 1 ? 's' : ''} inscrito
						{alumnos.length !== 1 ? 's' : ''}
					</Typography>
				</ResultsHeader>

					{alumnos.length > 0 ? (
						<AlumnoListContainer>
							<AlumnoListHeader>
								<Typography variant='caption' fontWeight={600} color='text.secondary'
									style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
									Alumno
								</Typography>
							</AlumnoListHeader>
							{alumnos.map((enrollment) => (
								<AlumnoRow key={enrollment.id}>
									<AlumnoAvatar src={enrollment.alumnoAvatarUrl ?? undefined}>
										{getAlumnoInitials(enrollment.alumnoNombre)}
									</AlumnoAvatar>
									<AlumnoInfo>
										<Typography variant='body2' fontWeight={600} noWrap>
											{enrollment.alumnoNombre}
										</Typography>
										<Typography variant='caption' color='text.secondary' noWrap>
											{enrollment.alumnoEmail}
										</Typography>
									</AlumnoInfo>
									<Typography variant='caption' color='text.secondary' noWrap>
										Inscrito el {formatDate(enrollment.enrolledAt)}
									</Typography>
									<Tooltip title='Eliminar alumno de la materia' arrow>
										<IconButton
											size='small'
											color='error'
											onClick={() => onRemoveAlumno(enrollment)}
										>
											<DeleteRounded fontSize='small' />
										</IconButton>
									</Tooltip>
								</AlumnoRow>
							))}
						</AlumnoListContainer>
					) : (
						<EmptyStateContainer>
							<Typography variant='h6' color='text.secondary' gutterBottom>
								Sin alumnos inscritos
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								Comparte el código <strong>{materia.codigo}</strong> con tus alumnos para que puedan inscribirse.
							</Typography>
						</EmptyStateContainer>
					)}
				</>
			)}

		<SubirRecursoModal
			open={isSubirRecursoModalOpen}
			materiaNombre={materia.nombre}
			title={uploadTitle}
			onClose={onCloseModal}
			onConfirmar={onConfirmarSubida}
		/>

		<ConfirmarEliminarDialog
			open={pendingRemoveAlumno !== null}
			alumnoNombre={pendingRemoveAlumno?.alumnoNombre}
			materiaNombre={materia.nombre}
			onConfirm={onConfirmRemoveAlumno}
			onCancel={onCancelRemoveAlumno}
		/>

		<ConfirmarLiberarMateriaDialog
			open={releaseOpen}
			materiaNombre={materia.nombre}
			onConfirm={onConfirmLiberarMateria}
			onCancel={onCancelLiberarMateria}
		/>
	</PageContainer>
);
};

export default MateriaDetailPage;
